import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '@/lib/api';
import type {
  CreateSubscriptionDto,
  CreateSubscriptionResponse,
  Subscription,
  SubscriptionPlan,
  SubscriptionStoreConfig,
  UpdateSubscriptionStoreDto,
} from '@/modules/subscriptions/interfaces/subscriptions';
import type {
  OrgPaymentSettings,
  UpdatePaymentSettingsDto,
} from '@/modules/merch/interfaces/merch';

export interface PublicOrgSubscriptionsData {
  organization: {
    id: string;
    name: string;
    slug: string;
    logo_url?: string;
    primary_color?: string;
    secondary_color?: string;
  };
  store: SubscriptionStoreConfig;
  plans: SubscriptionPlan[];
}

const queryKeys = {
  publicOrg: (slug: string) => ['subscriptions', 'public', slug] as const,
  mySubscriptions: ['subscriptions', 'my'] as const,
  orgSubscriptions: (orgId: string) => ['subscriptions', 'org', orgId] as const,
  orgDetail: (orgId: string, id: string) => ['subscriptions', 'org', orgId, id] as const,
  detail: (id: string) => ['subscriptions', 'detail', id] as const,
  orgPlans: (orgId: string) => ['subscriptions', 'plans', orgId] as const,
  orgPlan: (orgId: string, planId: string) => ['subscriptions', 'plans', orgId, planId] as const,
  orgStore: (orgId: string) => ['subscriptions', 'store', orgId] as const,
  paymentSettings: (orgId: string) => ['organizations', orgId, 'payment-settings'] as const,
};

export const usePublicOrgSubscriptionsQuery = (
  slug?: string,
  initialData?: PublicOrgSubscriptionsData,
) =>
  useQuery({
    queryKey: queryKeys.publicOrg(slug || ''),
    queryFn: () => apiGet<PublicOrgSubscriptionsData>(`/public/subscriptions/${slug}`),
    enabled: Boolean(slug),
    initialData,
    staleTime: initialData ? 5 * 60 * 1000 : 0,
  });

export const useCreateOrgSubscriptionMutation = (slug: string) =>
  useMutation({
    mutationFn: (payload: { email: string; plan_id?: string; amount?: number }) =>
      apiPost<CreateSubscriptionResponse>(`/public/subscriptions/${slug}`, payload),
  });

export const useMySubscriptionsQuery = () =>
  useQuery({
    queryKey: queryKeys.mySubscriptions,
    queryFn: () => apiGet<Subscription[]>('/subscriptions'),
  });

export const useOrgSubscriptionsQuery = (orgId?: string) =>
  useQuery({
    queryKey: queryKeys.orgSubscriptions(orgId || ''),
    queryFn: () => apiGet<Subscription[]>(`/organizations/${orgId}/subscriptions`),
    enabled: Boolean(orgId),
  });

export const useOrgSubscriptionDetailQuery = (orgId?: string, subscriptionId?: string) =>
  useQuery({
    queryKey: queryKeys.orgDetail(orgId || '', subscriptionId || ''),
    queryFn: () =>
      apiGet<Subscription>(`/organizations/${orgId}/subscriptions/${subscriptionId}`),
    enabled: Boolean(orgId && subscriptionId),
  });

export const useSyncOrgSubscriptionMutation = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (subscriptionId: string) =>
      apiPost<Subscription>(`/organizations/${orgId}/subscriptions/${subscriptionId}/sync`),
    onSuccess: (data) => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.orgSubscriptions(orgId) });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.orgDetail(orgId, data.id),
        });
      }
    },
  });
};

export const useSubscriptionDetailQuery = (id?: string) =>
  useQuery({
    queryKey: queryKeys.detail(id || ''),
    queryFn: () => apiGet<Subscription>(`/subscriptions/${id}`),
    enabled: Boolean(id),
  });

export const useCancelSubscriptionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<Subscription>(`/subscriptions/${id}/cancel`),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.mySubscriptions });
      void queryClient.invalidateQueries({ queryKey: queryKeys.detail(id) });
    },
  });
};

export const useCancelOrgSubscriptionMutation = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (subscriptionId: string) =>
      apiPost<Subscription>(`/organizations/${orgId}/subscriptions/${subscriptionId}/cancel`),
    onSuccess: () => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.orgSubscriptions(orgId) });
      }
    },
  });
};

export const useOrgSubscriptionPlansQuery = (orgId?: string) =>
  useQuery({
    queryKey: queryKeys.orgPlans(orgId || ''),
    queryFn: () => apiGet<SubscriptionPlan[]>(`/organizations/${orgId}/subscription-plans`),
    enabled: Boolean(orgId),
  });

export const useOrgSubscriptionPlanQuery = (orgId?: string, planId?: string) =>
  useQuery({
    queryKey: queryKeys.orgPlan(orgId || '', planId || ''),
    queryFn: () =>
      apiGet<SubscriptionPlan>(`/organizations/${orgId}/subscription-plans/${planId}`),
    enabled: Boolean(orgId && planId),
  });

export const useCreateSubscriptionPlanMutation = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<SubscriptionPlan>) =>
      apiPost<SubscriptionPlan>(`/organizations/${orgId}/subscription-plans`, payload),
    onSuccess: () => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.orgPlans(orgId) });
      }
    },
  });
};

export const useUpdateSubscriptionPlanMutation = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ planId, payload }: { planId: string; payload: Partial<SubscriptionPlan> }) =>
      apiPut<SubscriptionPlan>(`/organizations/${orgId}/subscription-plans/${planId}`, payload),
    onSuccess: (_data, variables) => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.orgPlans(orgId) });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.orgPlan(orgId, variables.planId),
        });
      }
    },
  });
};

export const useDeactivatePlanMutation = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (planId: string) =>
      apiDelete<SubscriptionPlan>(`/organizations/${orgId}/subscription-plans/${planId}`),
    onSuccess: () => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.orgPlans(orgId) });
      }
    },
  });
};

export const useReactivatePlanMutation = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (planId: string) =>
      apiPatch<SubscriptionPlan>(
        `/organizations/${orgId}/subscription-plans/${planId}/reactivate`,
        {},
      ),
    onSuccess: () => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.orgPlans(orgId) });
      }
    },
  });
};

export const useOrgSubscriptionStoreQuery = (orgId?: string) =>
  useQuery({
    queryKey: queryKeys.orgStore(orgId || ''),
    queryFn: () =>
      apiGet<SubscriptionStoreConfig>(`/organizations/${orgId}/subscription-store`),
    enabled: Boolean(orgId),
  });

export const useUpdateSubscriptionStoreMutation = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateSubscriptionStoreDto) =>
      apiPut<SubscriptionStoreConfig>(`/organizations/${orgId}/subscription-store`, payload),
    onSuccess: () => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.orgStore(orgId) });
      }
    },
  });
};

export const useSubscriptionStorePreviewQuery = (orgId?: string) =>
  useQuery({
    queryKey: [...queryKeys.orgStore(orgId || ''), 'preview'] as const,
    queryFn: () =>
      apiGet<PublicOrgSubscriptionsData>(`/organizations/${orgId}/subscription-store/preview`),
    enabled: Boolean(orgId),
  });

export const usePaymentSettingsQuery = (orgId?: string) =>
  useQuery({
    queryKey: queryKeys.paymentSettings(orgId || ''),
    queryFn: () => apiGet<OrgPaymentSettings>(`/organizations/${orgId}/payment-settings`),
    enabled: Boolean(orgId),
  });

export const useUpdatePaymentSettingsMutation = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdatePaymentSettingsDto) =>
      apiPut<OrgPaymentSettings>(`/organizations/${orgId}/payment-settings`, payload),
    onSuccess: () => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.paymentSettings(orgId) });
      }
    },
  });
};

/** @deprecated Use usePublicOrgSubscriptionsQuery instead */
export const usePlans = () => {
  const query = useQuery({
    queryKey: ['subscriptions', 'plans', 'legacy'],
    queryFn: async () => [] as SubscriptionPlan[],
  });
  return { data: query.data ?? [], isLoading: query.isLoading, error: query.error };
};

/** @deprecated Use useOrgSubscriptionsQuery instead */
export const useSubscriptions = (organizationId?: string) => {
  const query = useOrgSubscriptionsQuery(organizationId);

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: () => void query.refetch(),
  };
};

/** @deprecated Use useOrgSubscriptionDetailQuery instead */
export const useSubscription = (id: string | undefined, organizationId?: string) => {
  const query = useOrgSubscriptionDetailQuery(organizationId, id);
  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: () => void query.refetch(),
  };
};

export type { CreateSubscriptionDto, CreateSubscriptionResponse };
